import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import {
  CheckboxComponent,
  ConfirmDialogComponent,
  ConfirmDialogResult,
  InputComponent,
  TextWithShowMoreComponent,
  TooltipModule,
} from '@libs/ui-lib';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DateTime } from 'luxon';
import { Dialog } from '@angular/cdk/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { PatientAlertV2, PmsType } from '@ffd/api-client';
import { TextareaWithAIComponent } from '@libs/textarea-with-ai';
import { Store } from '@ngrx/store';
import { selectSiteSettingsPmsType } from '@libs/store';
import { HealthHistoryDatePickerComponent } from '../health-history-date-picker/health-history-date-picker.component';
import { PatientAlertsTabService } from '../patient-alerts-tab/patient-alerts-tab.service';

export interface PatientAlertsItemSaveData {
  isNewItem: boolean;
  patientAlert: PatientAlertV2;
}

interface PatientAlertValidationErrors {
  description: boolean;
  dateRange: boolean;
  showIn: boolean;
  note: boolean;
  startDateAfterEndDate: boolean;
}

@Component({
  selector: 'lib-patient-alerts-item-edit',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TextWithShowMoreComponent,
    SvgIconComponent,
    HealthHistoryDatePickerComponent,
    ReactiveFormsModule,
    LottieComponent,
    CheckboxComponent,
    TextareaWithAIComponent,
    InputComponent,
    TooltipModule,
  ],
  templateUrl: './patient-alerts-item-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientAlertsItemEditComponent implements OnInit {
  private readonly patientAlertsTabService = inject(PatientAlertsTabService);
  private readonly store = inject(Store);

  public readonly patientAlertsItem = input.required<PatientAlertV2>();
  public readonly isEditMode = input.required<boolean>();
  public readonly isNewItem = input.required<boolean>();
  public readonly isLoading = input.required<boolean>();
  public readonly deletePatientAlertEnabled = input<boolean>(true);

  public cancelButtonClicked = output<void>();
  public cancelButtonClickedNewItem = output<void>();
  public deleteButtonClicked = output<string>();
  public saveButtonClicked = output<PatientAlertsItemSaveData>();

  private readonly dialog = inject(Dialog);
  private readonly destroyRef = inject(DestroyRef);

  public readonly isOpenDental = computed(() => {
    const pmsType = this.store.selectSignal(selectSiteSettingsPmsType)();

    return pmsType === PmsType.OPEN_DENTAL;
  });

  public readonly createDateFormatted = computed<string>(() => {
    const iso = this.patientAlertsItem().createDate;
    return iso ? DateTime.fromISO(iso).toFormat('LL/dd/yyyy') : '';
  });

  public isVoiceTyping = signal<boolean>(false);

  public formGroup = new FormGroup({
    descriptionText: new FormControl<string>(''),
    startDatePicker: new FormControl<DateTime | null>(null),
    endDatePicker: new FormControl<DateTime | null>(null),
    alwaysOnCheckBox: new FormControl<boolean>(false),
    scheduleCheckBox: new FormControl<boolean>(true),
    patientChartCheckBox: new FormControl<boolean>(true),
    noteTextArea: new FormControl<string>(''),
  });

  public readonly editText = signal<boolean>(false);
  public readonly validationErrors = signal<PatientAlertValidationErrors>({
    description: false,
    dateRange: false,
    showIn: false,
    note: false,
    startDateAfterEndDate: false,
  });
  public readonly saveClicked = signal<boolean>(false);

  public isDeleteDisabled = computed(() => {
    return this.isLoading() || !this.deletePatientAlertEnabled();
  });

  public readonly lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  public readonly DESCRIPTION_TEXT_MAX_LENGTH = 64;

  constructor() {
    effect(() => {
      if (this.isOpenDental()) {
        untracked(() => {
          const previousValue = this.validationErrors();
          this.validationErrors.set({ ...previousValue, description: false });
        });
      }
    });
  }

  public ngOnInit(): void {
    this.initFormValues();

    this.updateDatePickersEnabledState(this.formGroup.get('alwaysOnCheckBox')?.value);

    this.formGroup
      .get('alwaysOnCheckBox')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isAlwaysOn) => {
        this.updateDatePickersEnabledState(isAlwaysOn);
      });

    this.formGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((form) => {
      this.patientAlertsTabService.setFormDirtyStateChanged(this.formGroup.dirty);

      this.validationErrors.set({
        description: !this.isOpenDental() && !form.descriptionText,
        dateRange: this.isOpenDental() ? !form.startDatePicker : !form.alwaysOnCheckBox && !(form.startDatePicker && form.endDatePicker),
        showIn: !form.scheduleCheckBox && !form.patientChartCheckBox,
        note: !form.noteTextArea,
        startDateAfterEndDate: !!(form.startDatePicker && form.endDatePicker && form.startDatePicker > form.endDatePicker),
      });
    });
  }

  public onCancelClick(): void {
    if (this.formGroup.dirty) {
      this.openCancelConfirmationDialog();
    } else {
      this.cancelEditing();
    }
  }

  public closeTextarea(): void {
    this.editText.set(false);
  }

  public onDeleteClick(): void {
    const dialogRef = this.dialog.open<ConfirmDialogResult>(ConfirmDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      panelClass: ['w-[480px]'],
      disableClose: true,
      data: {
        title: 'health_history_patient_alerts_tab_delete_modal_title',
        description: 'health_history_health_history_tab_item_edit_delete_modal_description',
        cancelLabel: 'health_history_health_history_tab_item_edit_delete_modal_cancel',
        confirmLabel: 'health_history_health_history_tab_item_edit_delete_modal_delete',
        destructive: true,
      },
    });

    dialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result?.confirm) {
        this.deleteButtonClicked.emit(this.patientAlertsItem().id);
      }
    });
  }

  public onSaveClick(): void {
    if (
      this.validationErrors().description ||
      this.validationErrors().dateRange ||
      this.validationErrors().showIn ||
      this.validationErrors().note ||
      this.validationErrors().startDateAfterEndDate
    ) {
      this.saveClicked.set(true);
      return;
    }

    const values = this.formGroup.getRawValue();
    this.patientAlertsTabService.setFormDirtyStateChanged(false);

    this.saveButtonClicked.emit({
      isNewItem: this.isNewItem(),
      patientAlert: {
        id: this.patientAlertsItem().id,
        patientId: this.patientAlertsItem().patientId,
        description: values.descriptionText ?? undefined,
        startDate: values.startDatePicker ? values.startDatePicker.toISODate() ?? undefined : undefined,
        endDate: values.endDatePicker ? values.endDatePicker.toISODate() ?? undefined : undefined,
        createDate: this.patientAlertsItem().createDate ?? DateTime.now().toISODate() ?? '',
        isAlways: values.alwaysOnCheckBox ?? false,
        showApptSymbol: this.patientAlertsItem().showApptSymbol,
        showInPatientChart: values.patientChartCheckBox ?? false,
        showInSchedule: values.scheduleCheckBox ?? false,
        provisionalStatus: this.patientAlertsItem().provisionalStatus,
        noteText: values.noteTextArea ?? undefined,
      },
    });
  }

  protected onVoiceTypingStatusChange(inProgress: boolean): void {
    this.isVoiceTyping.set(inProgress);
    this.patientAlertsTabService.setFormDirtyStateChanged(true);
  }

  private openCancelConfirmationDialog(): void {
    const dialogRef = this.dialog.open<ConfirmDialogResult>(ConfirmDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      panelClass: ['w-[480px]'],
      disableClose: true,
      data: {
        title: 'health_history_health_history_tab_item_edit_cancel_modal_title',
        description: 'health_history_health_history_tab_item_edit_cancel_modal_description',
        cancelLabel: this.isNewItem()
          ? 'health_history_health_history_tab_item_edit_cancel_modal_continue_add'
          : 'health_history_health_history_tab_item_edit_cancel_modal_continue',
        confirmLabel: 'health_history_health_history_tab_item_edit_cancel_modal_discard',
      },
    });

    dialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (!result?.confirm) {
        return;
      }

      this.patientAlertsTabService.setFormDirtyStateChanged(false);
      this.cancelEditing();
    });
  }

  private cancelEditing(): void {
    if (this.isNewItem()) {
      this.cancelButtonClickedNewItem.emit();
    }

    this.cancelButtonClicked.emit();
  }

  private updateDatePickersEnabledState(isAlwaysOn: boolean | null | undefined): void {
    if (isAlwaysOn) {
      this.formGroup.get('startDatePicker')?.disable();
      this.formGroup.get('endDatePicker')?.disable();
    } else {
      this.formGroup.get('startDatePicker')?.enable();
      this.formGroup.get('endDatePicker')?.enable();
    }
  }

  private initFormValues(): void {
    this.formGroup.setValue({
      descriptionText: this.patientAlertsItem().description ?? '',
      startDatePicker: this.patientAlertsItem().startDate ? DateTime.fromISO(this.patientAlertsItem().startDate!) : null,
      endDatePicker: this.patientAlertsItem().endDate ? DateTime.fromISO(this.patientAlertsItem().endDate!) : null,
      alwaysOnCheckBox: this.patientAlertsItem().isAlways,
      scheduleCheckBox: this.patientAlertsItem().showInSchedule,
      patientChartCheckBox: this.patientAlertsItem().showInPatientChart,
      noteTextArea: this.patientAlertsItem().noteText ?? '',
    });

    this.validationErrors.set({
      description: !this.isOpenDental() && !this.formGroup.controls.descriptionText.value,
      dateRange:
        !this.formGroup.controls.alwaysOnCheckBox.value &&
        !(this.formGroup.controls.startDatePicker.value && this.formGroup.controls.endDatePicker.value),
      showIn: !this.formGroup.controls.scheduleCheckBox.value && !this.formGroup.controls.patientChartCheckBox.value,
      note: !this.formGroup.controls.noteTextArea.value,
      startDateAfterEndDate: !!(
        this.formGroup.controls.startDatePicker.value &&
        this.formGroup.controls.endDatePicker.value &&
        this.formGroup.controls.startDatePicker.value > this.formGroup.controls.endDatePicker.value
      ),
    });
  }
}
